import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { LoaderCircle, LoaderIcon } from 'lucide-react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-body font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:w-[1em] [&_svg]:h-[1em] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-brand text-white hover:bg-brand-hover',
        light: 'bg-white text-black hover:bg-white/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-brand bg-white text-brand hover:bg-brand-light',
        secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200',
        ghost:
          'text-white/60 hover:bg-white/10 hover:text-white border-white/20 border',
        link: 'text-brand underline-offset-4 hover:underline',
        accent: 'bg-accent text-accent-foreground hover:bg-accent-hover',
        reminder:
          'bg-[#FF55371A] text-[#FF5537] hover:underline rounded-[40px] px-4',
        ghost_2: 'text-gray-400 hover:text-gray-600',
        outline_2:
          'border border-gray-600 text-gray-400 hover:text-white hover:border-gray-300',
        outline_dark: 'border bg-accent',
        'cyber-ghost': 'text-gray-400 hover:text-gold',
        'cyber-light': 'relative border-cyberpunk text-cyber font-bold',
        rainbow:
          'bg-gradient-to-r from-[#FD12E4] to-[#15F1FD] text-white font-medium',
        transparent: 'bg-white/10 hover:bg-white/20 text-white',
        active: 'bg-gold text-black hover:bg-gold-light font-semibold',
      },
      size: {
        default: 'h-10 px-base py-sm rounded-lg',
        xs: 'h-6 px-2 py-2 rounded-sm text-xs',
        sm: 'h-9 px-sm py-xs rounded-base text-sm',
        lg: 'h-11 px-xl py-base rounded-xl',
        xl: 'h-12 px-2xl py-base rounded-2xl',
        icon: 'h-10 w-10 rounded-lg',
        custom: '', // 不添加任何默认样式
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: React.ReactNode
  loading?: boolean
  iconPosition?: 'start' | 'end'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      color,
      icon,
      iconPosition,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    const nextClassName = React.useMemo(() => {
      return cn(className, color && `text-${color}`, color && `border-${color}`)
    }, [className, color])

    const iconNode: React.ReactNode = loading ? (
      <LoaderCircle className="mr-2 size-4 animate-spin" />
    ) : (
      icon
    )

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          iconPosition === 'end' && 'flex-row-reverse',
          '[&_svg]:size-[1em]',
          nextClassName,
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {iconNode ? iconNode : null}
            {children}
          </>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

const IconButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { active?: boolean }
>(
  (
    { children, loading, variant = 'ghost', disabled, active, ...props },
    ref,
  ) => (
    <Button
      variant={variant}
      size="icon"
      disabled={disabled || loading}
      ref={ref}
      {...props}
    >
      {loading && <LoaderIcon className="animate-spin" />}
      {!loading && children}
    </Button>
  ),
)

IconButton.displayName = 'IconButton'

export { IconButton, Button, buttonVariants }
